
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'reference',
  title: 'Changelog',
  section: 'Changelog',
  description: 'Tracks changes to Plaid Core Exchange',
  parentTocLevel: 4,
  childTocLevel: 5
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Plaid Core Exchange Changelog' subtitle={metadata.description} mdxType="Header" />
    <p>{`See the following changelist for updates to the Core Exchange specification. Core Exchange uses the same version numbers as FDX.`}</p>
    <h4 {...{
      "id": "investment-transaction-and-fhsa-update"
    }}>{`Investment transaction and FHSA update`}</h4>
    <p>{`October 6, 2026`}</p>
    <ul>
      <li parentName="ul">{`Core Exchange 5.1–6.4 accepts `}<inlineCode parentName="li">{`AIRDROP`}</inlineCode>{`, `}<inlineCode parentName="li">{`FORKED`}</inlineCode>{`, `}<inlineCode parentName="li">{`MINED`}</inlineCode>{`, and `}<inlineCode parentName="li">{`STAKED`}</inlineCode>{` as investment transfers, and `}<inlineCode parentName="li">{`WITHDRAWAL`}</inlineCode>{` as an investment cash transaction.`}</li>
      <li parentName="ul">{`Core Exchange 6.3–6.4 accepts `}<inlineCode parentName="li">{`FIRSTHOMESAVINGSACCOUNT`}</inlineCode>{` under `}<inlineCode parentName="li">{`INVESTMENT_ACCOUNT`}</inlineCode>{` when the account holds investments. Cash-only FHSAs remain under `}<inlineCode parentName="li">{`DEPOSIT_ACCOUNT`}</inlineCode>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "v64-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/6.4"
      }}>{`v6.4 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "new-features"
    }}>{`New Features`}</h5>
    <p>{`v6.4 aligns Core Exchange with FDX 6.4. All changes are additive and optional — there are no breaking changes, no new required fields, and no changes to existing endpoint signatures.`}</p>
    <p><strong parentName="p">{`Interest Rate Details`}</strong>{`: Added optional interest-rate reporting on deposit, investment, loan, and line-of-credit accounts:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`earnedInterest`}</inlineCode>{` on deposit and investment accounts, and `}<inlineCode parentName="li">{`chargedInterest`}</inlineCode>{` on loan and line-of-credit accounts (new `}<inlineCode parentName="li">{`InterestRate`}</inlineCode>{` object, with `}<inlineCode parentName="li">{`compoundingPeriod`}</inlineCode>{` via the new `}<inlineCode parentName="li">{`CompoundingPeriod`}</inlineCode>{` enum)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`InterestRateType`}</inlineCode>{` adds the `}<inlineCode parentName="li">{`INDEXED`}</inlineCode>{` value`}</li>
    </ul>
    <p><strong parentName="p">{`Enhanced Deposit Accounts`}</strong>{`: Added overdraft reporting — `}<inlineCode parentName="p">{`overdraftOptIn`}</inlineCode>{`, `}<inlineCode parentName="p">{`overdrafted`}</inlineCode>{`, `}<inlineCode parentName="p">{`overdraftProtectionFunded`}</inlineCode>{`, `}<inlineCode parentName="p">{`overdraftFundingSources`}</inlineCode>{` (new `}<inlineCode parentName="p">{`FundingSource`}</inlineCode>{` object), and `}<inlineCode parentName="p">{`underArbitration`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Enhanced Line-of-Credit Accounts`}</strong>{`: Added `}<inlineCode parentName="p">{`bills`}</inlineCode>{` and `}<inlineCode parentName="p">{`scheduledPayments`}</inlineCode>{` (new `}<inlineCode parentName="p">{`Bills`}</inlineCode>{`, `}<inlineCode parentName="p">{`ScheduledPayments`}</inlineCode>{`, and `}<inlineCode parentName="p">{`ScheduledPaymentType`}</inlineCode>{` schemas), plus additional APR fields and balance breakdowns.`}</p>
    <p><strong parentName="p">{`Loan Accounts`}</strong>{`: Added `}<inlineCode parentName="p">{`annualPercentageRate`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Account Descriptors`}</strong>{`: Added `}<inlineCode parentName="p">{`productId`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "core-exchange-ip-allowlist-update"
    }}>{`Core Exchange IP Allowlist Update`}</h4>
    <p>{`October 15, 2025`}</p>
    <ul>
      <li parentName="ul">{`A new range of Plaid IPs has been added for Core Exchange. Update your allowlist to include the following:`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`162.120.88.0/22`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`2602:F74B::/40`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <h4 {...{
      "id": "v63-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/6.3"
      }}>{`v6.3 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "new-features-1"
    }}>{`New Features`}</h5>
    <p><strong parentName="p">{`Enhanced Pagination`}</strong>{`: Added `}<inlineCode parentName="p">{`pageKey`}</inlineCode>{` query parameter and `}<inlineCode parentName="p">{`nextPageKey`}</inlineCode>{` response property for improved pagination on the following endpoints:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`GET /accounts`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`GET /accounts/{accountId}/asset-transfer-networks`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`GET /accounts/{accountId}/payment-networks`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`GET /accounts/{accountId}/statements`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`GET /accounts/{accountId}/transactions`}</inlineCode></li>
    </ul>
    <p>{`The `}<inlineCode parentName="p">{`offset`}</inlineCode>{` query parameter and the `}<inlineCode parentName="p">{`nextOffset`}</inlineCode>{` response property are deprecated in favor of `}<inlineCode parentName="p">{`pageKey`}</inlineCode>{` and `}<inlineCode parentName="p">{`nextPageKey`}</inlineCode>{`. Both are still accepted and will be removed in a future major release.`}</p>
    <p><strong parentName="p">{`New Deposit Account Types`}</strong>{`: Added support for specialized savings accounts:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`FIRSTHOMESAVINGSACCOUNT`}</inlineCode>{` - First Home Savings Account type`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`HIGHINTERESTSAVINGSACCOUNT`}</inlineCode>{` - High Interest Savings Account type`}</li>
    </ul>
    <p><strong parentName="p">{`Enhanced Contact Information`}</strong>{`: Improved contact data support with new fields:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Account Holder Relationships`}</strong>{`: Added `}<inlineCode parentName="li">{`AUTHORIZED_SIGNER`}</inlineCode>{` relationship type for account holders`}</li>
      <li parentName="ul"><strong parentName="li">{`Delivery Address Enhancements`}</strong>{`:`}
        <ul parentName="li">
          <li parentName="ul">{`Added `}<inlineCode parentName="li">{`type`}</inlineCode>{` property (`}<inlineCode parentName="li">{`DeliveryAddressType`}</inlineCode>{`: `}<inlineCode parentName="li">{`BUSINESS`}</inlineCode>{`, `}<inlineCode parentName="li">{`DELIVERY`}</inlineCode>{`, `}<inlineCode parentName="li">{`HOME`}</inlineCode>{`, `}<inlineCode parentName="li">{`MAILING`}</inlineCode>{`) to identify the location type of an address`}</li>
          <li parentName="ul">{`Added `}<inlineCode parentName="li">{`primary`}</inlineCode>{` property to identify the primary contact address`}</li>
        </ul>
      </li>
      <li parentName="ul"><strong parentName="li">{`Phone Number Enhancements`}</strong>{`:`}
        <ul parentName="li">
          <li parentName="ul">{`New phone types: `}<inlineCode parentName="li">{`BOTH`}</inlineCode>{`, `}<inlineCode parentName="li">{`PERSONAL`}</inlineCode></li>
          <li parentName="ul">{`Added `}<inlineCode parentName="li">{`network`}</inlineCode>{` property for mobile network information`}</li>
          <li parentName="ul">{`Added `}<inlineCode parentName="li">{`primary`}</inlineCode>{` property to identify primary contact numbers`}</li>
          <li parentName="ul">{`Enhanced country code validation with new pattern `}<inlineCode parentName="li">{`^\\\\+?[1-9][0-9]{0,2}$`}</inlineCode></li>
          <li parentName="ul">{`Deprecated the `}<inlineCode parentName="li">{`CELL`}</inlineCode>{` phone type, replaced by the `}<inlineCode parentName="li">{`CELLULAR`}</inlineCode>{` value on the new `}<inlineCode parentName="li">{`network`}</inlineCode>{` property`}</li>
        </ul>
      </li>
    </ul>
    <p><strong parentName="p">{`Enhanced Loan Account Details`}</strong>{`: Added optional properties for loan accounts:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`currentSchool`}</inlineCode>{` - Current educational institution for student loans`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`loanProviderName`}</inlineCode>{` - Name of the loan provider`}</li>
    </ul>
    <p><strong parentName="p">{`Transaction Enhancements`}</strong>{`:`}</p>
    <ul>
      <li parentName="ul">{`Added `}<inlineCode parentName="li">{`cardNumberDisplay`}</inlineCode>{` property for all transaction types to show masked card numbers`}</li>
      <li parentName="ul">{`Added date filtering with `}<inlineCode parentName="li">{`startDate`}</inlineCode>{` and `}<inlineCode parentName="li">{`endDate`}</inlineCode>{` query parameters for statements and transactions endpoints`}</li>
    </ul>
    <h5 {...{
      "id": "breaking-changes"
    }}>{`Breaking Changes`}</h5>
    <p><strong parentName="p">{`Contact Phone Number Field Changes`}</strong>{`: `}<inlineCode parentName="p">{`telephones[].country`}</inlineCode>{` now requires an optional leading `}<inlineCode parentName="p">{`+`}</inlineCode>{` followed by 1-3 digits with no leading zero (pattern `}<inlineCode parentName="p">{`^\\\\+?[1-9][0-9]{0,2}$`}</inlineCode>{`), and its maximum length increased from 3 to 4 characters to accommodate the `}<inlineCode parentName="p">{`+`}</inlineCode>{`.`}</p>
    <h5 {...{
      "id": "other-changes"
    }}>{`Other Changes`}</h5>
    <p><strong parentName="p">{`Schema Cleanup`}</strong>{`: The `}<inlineCode parentName="p">{`telephones[].type`}</inlineCode>{` field now references `}<inlineCode parentName="p">{`TelephoneNumberPurpose`}</inlineCode>{` instead of `}<inlineCode parentName="p">{`TelephoneNumberType`}</inlineCode>{`, which has been removed. Every previously valid value remains valid, so no action is required.`}</p>
    <h4 {...{
      "id": "v62-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/6.2"
      }}>{`v6.2 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "new-features-2"
    }}>{`New Features`}</h5>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Digital Wallet Support`}</strong>{`: Added the `}<inlineCode parentName="li">{`DIGITAL_WALLET`}</inlineCode>{` account category to the `}<inlineCode parentName="li">{`/accounts`}</inlineCode>{` endpoint response`}</li>
      <li parentName="ul"><strong parentName="li">{`New Currency Support`}</strong>{`: Added support for `}<inlineCode parentName="li">{`ZWG`}</inlineCode>{` (Zimbabwean Gold) currency code across all account types, transactions, and investment holdings`}</li>
      <li parentName="ul"><strong parentName="li">{`Investment Account Enhancement`}</strong>{`: Added `}<inlineCode parentName="li">{`PREPAID`}</inlineCode>{` account type for investment accounts`}</li>
      <li parentName="ul"><strong parentName="li">{`Digital Wallet Account Descriptor`}</strong>{`: New `}<inlineCode parentName="li">{`DigitalWalletDescriptor`}</inlineCode>{` schema added to the `}<inlineCode parentName="li">{`/accounts`}</inlineCode>{` endpoint response`}</li>
    </ul>
    <p><strong parentName="p">{`Statement Status Cleanup`}</strong>{`: Removed the enum constraint on statement `}<inlineCode parentName="p">{`status`}</inlineCode>{`, which had restricted the field to `}<inlineCode parentName="p">{`AVAILABLE`}</inlineCode>{`, `}<inlineCode parentName="p">{`PROCESSING`}</inlineCode>{`, and `}<inlineCode parentName="p">{`FAILED`}</inlineCode>{`. Those values remain valid; the field is now an unconstrained string.`}</p>
    <h4 {...{
      "id": "v61-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/6.1"
      }}>{`v6.1 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "fdx-changelist"
    }}>{`FDX changelist`}</h5>
    <ul>
      <li parentName="ul">{`Minor updates and improvements to existing schemas`}</li>
    </ul>
    <h5 {...{
      "id": "plaid-changelist"
    }}>{`Plaid changelist`}</h5>
    <ul>
      <li parentName="ul">{`Internal improvements and optimizations`}</li>
    </ul>
    <h4 {...{
      "id": "v60-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/6.0"
      }}>{`v6.0 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "fdx-changelist-1"
    }}>{`FDX changelist`}</h5>
    <p><inlineCode parentName="p">{`US_FEDNOW`}</inlineCode>{` (Federal Reserve Instant Payment System) is now an accepted `}<inlineCode parentName="p">{`PaymentNetworkType`}</inlineCode>{` in the `}<inlineCode parentName="p">{`/payment-networks`}</inlineCode>{` endpoint`}</p>
    <p>{`Added `}<inlineCode parentName="p">{`GET /accounts/{accountId}/asset-transfer-networks`}</inlineCode>{`, which returns the asset transfer networks (such as ACATS) supported by an account.`}</p>
    <h6 {...{
      "id": "breaking-changes-1"
    }}>{`Breaking changes`}</h6>
    <ul>
      <li parentName="ul">{`In the responses for the `}<inlineCode parentName="li">{`/accounts`}</inlineCode>{` and `}<inlineCode parentName="li">{`/accounts/{accountId}`}</inlineCode>{` endpoints, the account object is no longer wrapped with a named property (ex. `}<inlineCode parentName="li">{`depositAccount`}</inlineCode>{`).
The discriminator value `}<inlineCode parentName="li">{`accountCategory`}</inlineCode>{` is now required on each account type.`}</li>
    </ul>
    <p>{`For `}<inlineCode parentName="p">{`/accounts`}</inlineCode>{`, the `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` may be any of the following:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`ANNUITY_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`COMMERCIAL_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`DEPOSIT_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`INSURANCE_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`INVESTMENT_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`LOAN_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`LOC_ACCOUNT`}</inlineCode></li>
    </ul>
    <p>{`For `}<inlineCode parentName="p">{`/accounts/{accountId}`}</inlineCode>{`, which returns full account details, the `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` may be any of the following:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`DEPOSIT_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`INVESTMENT_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`LOAN_ACCOUNT`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`LOC_ACCOUNT`}</inlineCode></li>
    </ul>
    <p>{`(The `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` field is new in v6.0. The categories it names correspond to the wrapper keys it replaces.)`}</p>
    <ul>
      <li parentName="ul">{`In the response for the `}<inlineCode parentName="li">{`/accounts/{accountId}/transactions`}</inlineCode>{` endpoint, the transaction object is no longer wrapped with a named property (ex. `}<inlineCode parentName="li">{`depositTransaction`}</inlineCode>{`).
The discriminator value `}<inlineCode parentName="li">{`accountCategory`}</inlineCode>{` is now required on each transaction object.`}</li>
    </ul>
    <p>{`An implementer’s mapping of each transaction must set the required `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` property.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` may be any of the following:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`DEPOSIT_ACCOUNT`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`INVESTMENT_ACCOUNT`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`LOAN_ACCOUNT`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`LOC_ACCOUNT`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`The `}<inlineCode parentName="p">{`holding`}</inlineCode>{` object in investment accounts now supports an array of `}<inlineCode parentName="p">{`securityIds`}</inlineCode>{` with `}<inlineCode parentName="p">{`id`}</inlineCode>{`/`}<inlineCode parentName="p">{`idType`}</inlineCode>{` pairs.
(These pairs have replaced the `}<inlineCode parentName="p">{`securityId`}</inlineCode>{`/`}<inlineCode parentName="p">{`securityIdType`}</inlineCode>{` keys as identifiers.)`}</p>
      </li>
    </ul>
    <h5 {...{
      "id": "plaid-changelist-1"
    }}>{`Plaid changelist`}</h5>
    <h6 {...{
      "id": "breaking-changes-2"
    }}>{`Breaking changes`}</h6>
    <p>{`In AccountWithDetails (response for `}<inlineCode parentName="p">{`/accounts/{accountId}`}</inlineCode>{` endpoint) removing the `}<inlineCode parentName="p">{`xxxAccount`}</inlineCode>{` properties and requiring discriminator value `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{`:
on each account type, one of `}<inlineCode parentName="p">{`DEPOSIT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="p">{`INVESTMENT_ACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="p">{`LOAN_ACCOUNT`}</inlineCode>{` or `}<inlineCode parentName="p">{`LOC_ACCOUNT`}</inlineCode>{`.`}</p>
    <p>{`An implementer’s mapping of each account must set the required `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` property.
The account object is no longer wrapped with a named property (ex. `}<inlineCode parentName="p">{`depositAccount`}</inlineCode>{`).`}</p>
    <h4 {...{
      "id": "v53-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/5.3"
      }}>{`v5.3 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "fdx-changelist-2"
    }}>{`FDX changelist`}</h5>
    <ul>
      <li parentName="ul">{`A new account type (`}<inlineCode parentName="li">{`CommercialAccountType`}</inlineCode>{`) was added.
The following account types are classified as commercial accounts:`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`COMMERCIALDEPOSIT`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`COMMERCIALINVESTMENT`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`COMMERCIALLOAN`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`COMMERCIALLINEOFCREDIT`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">{`Commercial account type includes a new descriptor: `}<inlineCode parentName="li">{`CommercialAccountDescriptor`}</inlineCode>{`.`}</li>
    </ul>
    <h6 {...{
      "id": "breaking-changes-3"
    }}>{`Breaking changes`}</h6>
    <ul>
      <li parentName="ul">{`The ISO 3166 country code standard was updated to ISO 3166-1 alpha-2.`}</li>
      <li parentName="ul">{`The list of ISO 4217 currency codes was updated. (As of January 1, 2023)`}</li>
      <li parentName="ul"><strong parentName="li">{`Note:`}</strong>{` Some country codes (`}<inlineCode parentName="li">{`AN`}</inlineCode>{`, `}<inlineCode parentName="li">{`CS`}</inlineCode>{`) and currency codes (`}<inlineCode parentName="li">{`HRK`}</inlineCode>{`) were removed in the updated version, making this a breaking change.
(Please see the `}<a parentName="li" {...{
          "href": "https://www.iso.org/standards.html"
        }}>{`ISO standards`}</a>{` for more information.)`}</li>
    </ul>
    <h5 {...{
      "id": "plaid-changelist-2"
    }}>{`Plaid changelist`}</h5>
    <h6 {...{
      "id": "breaking-changes-4"
    }}>{`Breaking changes`}</h6>
    <ul>
      <li parentName="ul">{`Plaid will not provide balance product support for the new `}<inlineCode parentName="li">{`CommercialAccountType`}</inlineCode>{`.`}</li>
    </ul>
    <h4 {...{
      "id": "v52-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/5.2"
      }}>{`v5.2 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "new-features-3"
    }}>{`New Features`}</h5>
    <p><strong parentName="p">{`Account Statements`}</strong>{`: Added `}<inlineCode parentName="p">{`GET /accounts/{accountId}/statements`}</inlineCode>{` and `}<inlineCode parentName="p">{`GET /accounts/{accountId}/statements/{statementId}`}</inlineCode>{` for retrieving account statements.`}</p>
    <p><strong parentName="p">{`Business Customer Support`}</strong>{`: Added `}<inlineCode parentName="p">{`BusinessCustomer`}</inlineCode>{` and `}<inlineCode parentName="p">{`BusinessOrConsumer`}</inlineCode>{` schemas so `}<inlineCode parentName="p">{`/customers/current`}</inlineCode>{` can represent a business, not just an individual consumer. Account holders can now include a `}<inlineCode parentName="p">{`businessCustomer`}</inlineCode>{`, and `}<inlineCode parentName="p">{`AccountHolder.name`}</inlineCode>{` is optional to support business-owned accounts.`}</p>
    <p><strong parentName="p">{`Investment Account Types`}</strong>{`: Added Canadian retirement and tax-advantaged account types (`}<inlineCode parentName="p">{`REGISTEREDRETIREMENTSAVINGSPLAN`}</inlineCode>{`, `}<inlineCode parentName="p">{`TAXFREESAVINGSACCOUNT`}</inlineCode>{`, `}<inlineCode parentName="p">{`LOCKEDINRETIREMENTACCOUNT`}</inlineCode>{`, and others).`}</p>
    <h4 {...{
      "id": "v51-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/5.1"
      }}>{`v5.1 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "fdx-changelist-3"
    }}>{`FDX changelist`}</h5>
    <ul>
      <li parentName="ul">{`For the endpoints that Plaid supports, FDX introduces minor enum, model, and formatting changes in this version.`}</li>
      <li parentName="ul">{`Added `}<inlineCode parentName="li">{`US_RTP`}</inlineCode>{` (The Clearing House Real-Time Payments network) as an accepted payment network type.`}</li>
    </ul>
    <h6 {...{
      "id": "breaking-changes-5"
    }}>{`Breaking changes:`}</h6>
    <ul>
      <li parentName="ul">{`None`}</li>
    </ul>
    <h5 {...{
      "id": "plaid-changelist-3"
    }}>{`Plaid changelist`}</h5>
    <ul>
      <li parentName="ul">{`Plaid introduces support and documentation for FDX Digital Assets.`}</li>
    </ul>
    <h4 {...{
      "id": "v50-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/5.0"
      }}>{`v5.0 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "fdx-changelist-4"
    }}>{`FDX changelist`}</h5>
    <ul>
      <li parentName="ul">{`For the endpoints that Plaid supports, FDX introduces minor enum, model, and formatting changes in this version.`}</li>
    </ul>
    <h6 {...{
      "id": "breaking-changes-6"
    }}>{`Breaking changes:`}</h6>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`state`}</inlineCode>{` property on an Address was renamed to the more inclusive `}<inlineCode parentName="li">{`region`}</inlineCode>{`.`}</li>
    </ul>
    <h5 {...{
      "id": "plaid-changelist-4"
    }}>{`Plaid changelist`}</h5>
    <ul>
      <li parentName="ul">{`Added `}<inlineCode parentName="li">{`AUTHORIZED_USER`}</inlineCode>{` as an account holder relationship type.`}</li>
    </ul>
    <h4 {...{
      "id": "v46-core-exchange-api-specification"
    }}><a parentName="h4" {...{
        "href": "https://plaid.com/core-exchange/docs/reference/4.6"
      }}>{`v4.6 Core Exchange API specification`}</a></h4>
    <h5 {...{
      "id": "fdx-changelist-5"
    }}>{`FDX changelist`}</h5>
    <ul>
      <li parentName="ul">{`None`}</li>
    </ul>
    <h5 {...{
      "id": "plaid-changelist-5"
    }}>{`Plaid changelist`}</h5>
    <ul>
      <li parentName="ul">{`Plaid introduces Core Exchange in this version by choosing a subset of FDX endpoints to support and adding Plaid-specific documentation.`}</li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "investment-transaction-and-fhsa-update",
        level: 4,
        title: "Investment transaction and FHSA update",
      },
{
        id: "v64-core-exchange-api-specification",
        level: 4,
        title: "v6.4 Core Exchange API specification",
      },
{
        id: "core-exchange-ip-allowlist-update",
        level: 4,
        title: "Core Exchange IP Allowlist Update",
      },
{
        id: "v63-core-exchange-api-specification",
        level: 4,
        title: "v6.3 Core Exchange API specification",
      },
{
        id: "v62-core-exchange-api-specification",
        level: 4,
        title: "v6.2 Core Exchange API specification",
      },
{
        id: "v61-core-exchange-api-specification",
        level: 4,
        title: "v6.1 Core Exchange API specification",
      },
{
        id: "v60-core-exchange-api-specification",
        level: 4,
        title: "v6.0 Core Exchange API specification",
      },
{
        id: "v53-core-exchange-api-specification",
        level: 4,
        title: "v5.3 Core Exchange API specification",
      },
{
        id: "v52-core-exchange-api-specification",
        level: 4,
        title: "v5.2 Core Exchange API specification",
      },
{
        id: "v51-core-exchange-api-specification",
        level: 4,
        title: "v5.1 Core Exchange API specification",
      },
{
        id: "v50-core-exchange-api-specification",
        level: 4,
        title: "v5.0 Core Exchange API specification",
      },
{
        id: "v46-core-exchange-api-specification",
        level: 4,
        title: "v4.6 Core Exchange API specification",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
